import { Injectable } from '@angular/core';
import { DataRepository, TableRepository, WorkspaceDatabaseKeys } from '@database/workspace';
import { DataModel, fileBooleanMappings, PrimaryKeyValueModel, SurroundingIntervalsModel, TableModel } from '@shared';
import { of, combineLatest, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BusinessLayerService } from '../../../services/business-layer.service';
import { IntervalChildData } from '../handlers/models';
import { IntervalTableEnum } from '../../../../dynamic-form/validations/models';
import { DataByTableTypeProvider } from '../../../services/data-by-table-type.provider';
@Injectable()
export class DeleteIntervalService {
	public constructor(
		private dataRepository: DataRepository,
		private businessLayerService: BusinessLayerService,
		private tableRepository: TableRepository,
		private dataByTableTypeProvider: DataByTableTypeProvider
	) { }

	public isDeleteStartAtZeroForbidden$(
		tableName: string,
		workfileId: string,
		projectNumber: string,
		entityName: string,
		entryId: string,
		childEntryId: number
	): Observable<boolean> {
		return this.dataByTableTypeProvider.isDeleteStartAtZeroForbidden$(
			tableName,
			workfileId,
			projectNumber,
			entityName,
			Number(entryId),
			childEntryId
		);
	}

	public isGapInData$(
		tableName: string,
		workfileId: string,
		projectNumber: string,
		entityName: string,
		entryId: string,
		childEntryId: number,
		depthFrom: number,
		depthTo: number
	): Observable<boolean> {
		return this.businessLayerService
			.getPrimaryKeyAndValue$(workfileId, projectNumber, entityName, Number(entryId))
			.pipe(
				switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
					this.dataRepository
						.getSurroundingIntervals$(
							tableName.toLowerCase(),
							projectNumber,
							primaryKeyValue,
							Number(childEntryId),
							depthFrom,
							depthTo
						)
						.pipe(
							map((surroundingIntervals: SurroundingIntervalsModel) => {
								if (surroundingIntervals.previousInterval && surroundingIntervals.nextInterval) {
									return (
										surroundingIntervals.previousInterval[WorkspaceDatabaseKeys.columns.depthTo] <
										surroundingIntervals.nextInterval[WorkspaceDatabaseKeys.columns.depthFrom]
									);
								}

								return false;
							})
						)
				)
			);
	}

	public originalHasQc$(
		tableName: string,
		workfileId: string,
		projectNumber: string,
		entityName: string,
		entryId: string,
		childEntryId: number,
		depthFrom: number,
		depthTo: number,
		qcRefTypes: string[]
	): Observable<boolean> {
		return combineLatest([
			this.dataByTableTypeProvider.getOutsideIntervalData$(
				tableName,
				workfileId,
				projectNumber,
				entityName,
				entryId,
				childEntryId.toString(),
				depthFrom,
				depthTo
			),
			this.dataRepository
				.getTableEntry$(tableName ? tableName : entityName, projectNumber, childEntryId)
				.pipe(
					switchMap((currentOriginalSample: DataModel) =>
						this.dataByTableTypeProvider.getParentSampleNumberLinkedData$(
							tableName,
							workfileId,
							projectNumber,
							entityName,
							entryId,
							childEntryId.toString(),
							currentOriginalSample[WorkspaceDatabaseKeys.columns.parentSampleNumber]
						)
					)
				)
		]).pipe(
			map(([outsideIntervalData, sampleLinkedData]) => {
				const intervalSamples = outsideIntervalData.filter((intervalEntry: DataModel) =>
					qcRefTypes.includes(intervalEntry.assay_sample_type_code)
				);
				const parentSampleLinkSamples = sampleLinkedData.filter((intervalEntry: DataModel) =>
					qcRefTypes.includes(intervalEntry.assay_sample_type_code)
				);

				return intervalSamples.length > 0 || parentSampleLinkSamples.length > 0;
			})
		);
	}

	public checkIntervalDataDependencies$(
		tableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[],
		childEntryId: number,
		depthFrom: number,
		depthTo: number
	): Observable<IntervalChildData[]> {
		return this.tableRepository.getIntervalTables$(this.getSkippedIntervalTables(tableName)).pipe(
			switchMap((tables: TableModel[]) => {
				if (tables.length === 0) {
					return of([]);
				}
				return combineLatest(
					tables.map((table: TableModel) =>
						this.dataRepository
							.getInsideIntervalData$(
								table.table_name.toLowerCase(),
								projectNumber,
								primaryKeyValue,
								childEntryId,
								depthFrom,
								depthTo
							)
							.pipe(
								map((tableData: DataModel[]) => {
									const filteredTd = tableData.filter((td: DataModel) =>
										tableName === IntervalTableEnum.HoleIntervalMajor
											? !fileBooleanMappings[td.is_minor]
											: fileBooleanMappings[td.is_minor]
									);
									return {
										tableName: table.table_name,
										tableHasData: filteredTd.length > 0
									};
								})
							)
					)
				)
			}
			)
		);
	}

	public isExternalCreatedRecord$(tableName: string, id: number): Observable<boolean> {
		return this.dataRepository
			.getTablePropertyValueById$(tableName, id, WorkspaceDatabaseKeys.columns.externalCreatedRecord)
			.pipe(
				map((externalCreatedRecord: string | null) =>
					externalCreatedRecord ? fileBooleanMappings[externalCreatedRecord] : false
				)
			);
	}

	private getSkippedIntervalTables(intervalTable: string): IntervalTableEnum[] {
		if (intervalTable === IntervalTableEnum.HoleIntervalMajor) {
			return [IntervalTableEnum.HoleIntervalMajor];
		}

		if (intervalTable === IntervalTableEnum.HoleIntervalMinor) {
			return [IntervalTableEnum.HoleIntervalMajor, IntervalTableEnum.HoleIntervalMinor];
		}

		return null;
	}
}
